import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Button, Typography, Select, MenuItem, useTheme, Theme } from '@mui/material';
import {
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Check as CheckIcon,
  VisibilityOff as VisibilityOffIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';

import {
  primaryActionButtonStyles,
  secondaryActionButtonStyles,
  dangerActionButtonStyles,
  responsivePrimaryButtonStyles,
} from './styles/common-style';

import { useIsMobile } from '@/hooks/use-is-mobile';

// Constants
const CONTAINER_HEIGHT = 72;
const BUTTON_HEIGHT = 40;
const NAV_BUTTON_MIN_WIDTH = 80;
const PAGE_BUTTON_SIZE = 40;
const BORDER_RADIUS_PILL = '50px';
const BORDER_RADIUS_MENU = '12px';

interface ApiResponseInfo {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly requestId?: string;
  readonly isLoading?: boolean;
}

interface EnhancedPaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly rowsPerPage: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
  readonly onRowsPerPageChange: (rowsPerPage: number) => void;
  readonly rowsPerPageOptions?: number[];
  readonly apiResponseInfo?: ApiResponseInfo;
  // Optional delete functionality
  readonly selectedCount?: number;
  readonly onDelete?: () => void;
  readonly showDeleteButton?: boolean;
  // Optional archive functionality
  readonly onArchive?: () => void;
  readonly onRestore?: () => void;
  readonly showArchiveButtons?: boolean;
  // Optional filter buttons for plan page
  readonly showFilterButtons?: boolean;
  readonly activeFilter?: 'confirmed' | 'unconfirmed' | 'archived' | 'active' | null;
  // Optional confirm functionality for plan page
  readonly onConfirmed?: () => void;
  readonly onUnconfirmed?: () => void;
  readonly onArchived?: () => void;
  readonly onActive?: () => void;
  readonly hasNegotiatingStatus?: boolean;
  // Optional scroll target ref for scrolling to top when page changes
  readonly scrollTargetRef?: React.RefObject<HTMLElement>;
  // Optional flag to show/hide records info section
  readonly showRecordsInfo?: boolean;
  readonly showPageNavigation?: boolean;
  readonly showPerPageSelector?: boolean;
}

// Helper function to generate visible page numbers
const getVisiblePages = (
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5
): number[] => {
  const pages: number[] = [];

  if (totalPages === 0) return pages;

  if (totalPages <= maxVisible) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    const start = Math.max(
      0,
      Math.min(currentPage - Math.floor(maxVisible / 2), totalPages - maxVisible)
    );
    const end = Math.min(start + maxVisible, totalPages);
    for (let i = start; i < end; i++) {
      pages.push(i);
    }
  }
  return pages;
};

// Count Pill Component - optimized Typography structure
const CountPill: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        backgroundColor: 'rgba(0, 78, 162, 0.1)',
        borderRadius: BORDER_RADIUS_PILL,
        border: '1px solid rgba(0, 78, 162, 0.2)',
        minWidth: '50px',
        height: '26px',
        mx: 0.5,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      {children}
    </Box>
  );
};

// Style factories for buttons
const createNavButtonStyles = (theme: Theme, disabled: boolean) => ({
  minWidth: NAV_BUTTON_MIN_WIDTH,
  height: BUTTON_HEIGHT,
  px: { xs: 1, sm: 1.5, md: 2 },
  py: { xs: 0.5, sm: 0.75, md: 1 },
  borderRadius: BORDER_RADIUS_PILL,
  color: disabled ? theme.palette.text.secondary : theme.palette.primary.main,
  border: '1px solid',
  borderColor: disabled ? theme.palette.text.secondary : theme.palette.primary.main,
  '&:hover': {
    backgroundColor: disabled ? 'transparent' : 'rgba(0, 78, 162, 0.1)',
    borderColor: disabled ? theme.palette.text.secondary : theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
});

const createPageButtonStyles = (theme: Theme, isActive: boolean) => ({
  minWidth: PAGE_BUTTON_SIZE,
  maxWidth: PAGE_BUTTON_SIZE,
  height: PAGE_BUTTON_SIZE,
  borderRadius: BORDER_RADIUS_PILL,
  border: '1px solid',
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1,
  textAlign: 'center',
  justifyContent: 'center',
  fontWeight: 'normal',
  ...(isActive
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
        },
      }
    : {
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'rgba(0, 78, 162, 0.1)',
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        },
      }),
  '&.Mui-disabled': {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  },
});

export const Pagination: React.FC<EnhancedPaginationProps> = ({
  currentPage,
  totalPages,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [20, 50, 100],
  apiResponseInfo,
  selectedCount = 0,
  onDelete,
  showDeleteButton = false,
  onArchive,
  onRestore,
  showArchiveButtons = false,
  showFilterButtons = false,
  onConfirmed,
  onUnconfirmed,
  onArchived,
  onActive,
  hasNegotiatingStatus = false,
  scrollTargetRef,
  showRecordsInfo = true,
  showPageNavigation = true,
  showPerPageSelector = true,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isLoading = apiResponseInfo?.isLoading ?? false;
  const isButtonDisabled = isLoading || selectedCount === 0;
  const safeTotalCount = Math.max(totalCount || 0, 0);
  const displayedMaxCount = Math.min(rowsPerPage, safeTotalCount);
  const previousPageRef = useRef<number>(currentPage);

  // Memoize visible pages to avoid recreating array on every render
  // Show 3 pages on mobile, 5 on desktop
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages, isMobile ? 3 : 5),
    [currentPage, totalPages, isMobile]
  );

  // Optimized scroll effect - prioritize scrollTargetRef, simple fallback
  useEffect(() => {
    if (previousPageRef.current === currentPage) return;
    previousPageRef.current = currentPage;

    const scrollTarget = scrollTargetRef?.current;
    if (!scrollTarget) {
      // Simple fallback: find sibling TableContainer
      const paginationElement = document.querySelector('[data-pagination-container]');
      const siblingTableContainer = paginationElement?.parentElement?.querySelector(
        '.MuiTableContainer-root'
      ) as HTMLElement | null;

      if (siblingTableContainer) {
        const timeoutId = window.setTimeout(() => {
          siblingTableContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
        return () => window.clearTimeout(timeoutId);
      }
      return;
    }

    // Use scrollTargetRef - preferred approach
    const timeoutId = window.setTimeout(() => {
      scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [currentPage, scrollTargetRef]);

  // Memoized handlers to avoid recreating functions
  const handlePreviousPage = useCallback(() => {
    onPageChange(currentPage - 1);
  }, [currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    onPageChange(currentPage + 1);
  }, [currentPage, onPageChange]);

  const handlePageClick = useCallback(
    (pageIndex: number) => {
      onPageChange(pageIndex);
    },
    [onPageChange]
  );

  return (
    <Box
      data-pagination-container
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 2,
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 0.5, sm: 1, md: 1 },
        backgroundColor: theme.palette.primary.contrastText,
        borderRadius: 1,
        flexWrap: showPageNavigation ? 'wrap' : { xs: 'nowrap', sm: 'wrap' },
        gap: { xs: showPageNavigation ? 1 : 0.5, sm: 2, md: 3 },
        border: `1px solid`,
        borderColor: theme.palette.divider,
        width: '100%',
        minHeight: showPageNavigation
          ? `${CONTAINER_HEIGHT}px`
          : { xs: 'auto', sm: `${CONTAINER_HEIGHT}px` },
        boxSizing: 'border-box',
        opacity: isLoading ? 0.6 : 1,
        transition: 'opacity 0.2s ease-in-out',
        // Removed pointerEvents: 'none' to allow interaction with disabled buttons
      }}
    >
      {/* Left side - Main controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: showPageNavigation ? 1 : 0.5, sm: 2, md: 3 },
          flexWrap: showPageNavigation ? 'wrap' : { xs: 'nowrap', sm: 'wrap' },
          flex: showPageNavigation ? 'auto' : { xs: 1, sm: 'auto' },
        }}
      >
        {/* Records info - Optional */}
        {showRecordsInfo && (
          <Typography
            variant='body2'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: theme.palette.text.secondary,
              lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
              fontWeight: 500,
              flexWrap: { xs: showPageNavigation ? 'wrap' : 'nowrap', md: 'nowrap' },
              fontSize: { xs: showPageNavigation ? '0.875rem' : '0.75rem', sm: '0.875rem' },
              whiteSpace: { xs: showPageNavigation ? 'normal' : 'nowrap', sm: 'normal' },
            }}
          >
            検索結果件数 <CountPill>{safeTotalCount.toLocaleString()}</CountPill>
            件中 <CountPill>{displayedMaxCount.toLocaleString()}</CountPill> 件
          </Typography>
        )}

        {/* Page navigation */}

        {showPageNavigation && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: { xs: 1, sm: 1, md: 1 },
              gap: { xs: 1, sm: 1, md: 1 },
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
          >
            {/* Previous button */}
            <Button
              variant='text'
              size='small'
              disabled={currentPage === 0 || isLoading}
              onClick={handlePreviousPage}
              sx={createNavButtonStyles(theme, currentPage === 0)}
            >
              前へ
            </Button>

            {/* Page numbers */}
            {visiblePages.map((pageIndex) => (
              <Button
                key={pageIndex}
                variant='outlined'
                size='small'
                disabled={isLoading}
                onClick={() => handlePageClick(pageIndex)}
                sx={createPageButtonStyles(theme, currentPage === pageIndex)}
              >
                {pageIndex + 1}
              </Button>
            ))}

            {/* Next button */}
            <Button
              variant='text'
              size='small'
              disabled={currentPage >= totalPages - 1 || isLoading}
              onClick={handleNextPage}
              sx={createNavButtonStyles(theme, currentPage >= totalPages - 1)}
            >
              次へ
            </Button>
          </Box>
        )}

        {/* Per page selector */}
        {showPerPageSelector && (
          <Box sx={{ flexShrink: 0 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              size='small'
              variant='outlined'
              disabled={isLoading}
              sx={{
                minWidth: { xs: 82, sm: 70, md: 80 },
                width: { xs: 'auto', sm: 'auto', md: 100 },
                height: {
                  xs: showPageNavigation ? `${BUTTON_HEIGHT}px` : '32px',
                  sm: `${BUTTON_HEIGHT}px`,
                },
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                fontSize: { xs: showPageNavigation ? '0.875rem' : '0.75rem', sm: '0.875rem' },
                marginLeft: { xs: 0.5, sm: 0, md: 1 },
                '& .MuiSelect-select': {
                  paddingRight: '24px !important',
                  padding: {
                    xs: showPageNavigation ? '10px 32px 10px 12px' : '6px 24px 6px 8px',
                    sm: '11px 32px 11px 16px',
                    md: '12px 32px 12px 20px',
                  },
                  height: {
                    xs: showPageNavigation ? `${BUTTON_HEIGHT}px` : '32px',
                    sm: `${BUTTON_HEIGHT}px`,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  boxSizing: 'border-box',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderRadius: BORDER_RADIUS_PILL,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.primary.main,
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                  color: theme.palette.divider,
                },
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      borderRadius: BORDER_RADIUS_MENU,
                      '& .MuiMenuItem-root': {
                        lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                        padding: { xs: '8px 16px', sm: '10px 18px', md: '12px 20px' },
                        minHeight: `${BUTTON_HEIGHT}px`,
                        height: `${BUTTON_HEIGHT}px`,
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: 'rgba(26, 28, 32, 0.08)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#D7E2FF',
                          color: '#0D2F60',
                          '&:hover': {
                            backgroundColor: '#D7E2FF',
                            color: '#0D2F60',
                          },
                        },
                      },
                    },
                  },
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}件
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      {/* Right side - Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 0.5, sm: 2 },
          alignItems: 'center',
          flexWrap: showPageNavigation ? 'wrap' : { xs: 'nowrap', sm: 'wrap' },
          justifyContent: { xs: 'center', sm: 'flex-end' },
          flexShrink: 0,
        }}
      >
        {/* Filter Buttons for Plan Page (optional) */}
        {showFilterButtons && (
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {onConfirmed && (
              <Button
                variant='outlined'
                startIcon={<CheckIcon />}
                onClick={onConfirmed}
                disabled={isButtonDisabled || hasNegotiatingStatus}
                sx={responsivePrimaryButtonStyles(theme)}
              >
                前日確認
              </Button>
            )}
            {onUnconfirmed && (
              <Button
                variant='outlined'
                startIcon={<VisibilityOffIcon />}
                onClick={onUnconfirmed}
                disabled={isButtonDisabled || hasNegotiatingStatus}
                sx={responsivePrimaryButtonStyles(theme)}
              >
                未確認
              </Button>
            )}
            {onArchived && (
              <Button
                variant='outlined'
                startIcon={<ArchiveIcon />}
                onClick={onArchived}
                disabled={isButtonDisabled}
                sx={responsivePrimaryButtonStyles(theme)}
              >
                アーカイブ
              </Button>
            )}
            {onActive && (
              <Button
                variant='outlined'
                startIcon={<RedoIcon />}
                onClick={onActive}
                disabled={isButtonDisabled}
                sx={responsivePrimaryButtonStyles(theme)}
              >
                アクティブ
              </Button>
            )}
          </Box>
        )}

        {/* Delete Button (optional) */}
        {showDeleteButton && onDelete && (
          <Button
            variant='contained'
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={selectedCount === 0 || isLoading}
            sx={dangerActionButtonStyles(theme)}
          >
            削除
          </Button>
        )}

        {/* Archive Buttons (optional) */}
        {showArchiveButtons && (
          <>
            {onArchive && (
              <Button
                variant='contained'
                startIcon={<ArchiveIcon />}
                onClick={onArchive}
                disabled={selectedCount === 0 || isLoading}
                sx={primaryActionButtonStyles(theme)}
              >
                アーカイブ
              </Button>
            )}
            {onRestore && (
              <Button
                variant='outlined'
                startIcon={<UnarchiveIcon />}
                onClick={onRestore}
                disabled={selectedCount === 0 || isLoading}
                sx={secondaryActionButtonStyles(theme)}
              >
                アクティブに戻す
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Pagination;
